import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Select, Space, Input, message, Spin } from 'antd';
import { TeamOutlined, ArrowLeftOutlined, EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authHeader } from '../../utils/api';
import SelectLocations from '../Client/Campaigns/NewCampaignUX/steps/SelectLocations';
import ChooseCampaignType from '../Client/Campaigns/NewCampaignUX/steps/ChooseCampaignType';
import ConfigureStopLogic from '../Client/Campaigns/NewCampaignUX/steps/ConfigureStopLogic';
import SelectStops from '../Client/Campaigns/NewCampaignUX/steps/SelectStops';
import ConfigureRouteLogic from '../Client/Campaigns/NewCampaignUX/steps/ConfigureRouteLogic';
import SelectRoutes from '../Client/Campaigns/NewCampaignUX/steps/SelectRoutes';
import ConfigureProximityIQ from '../Client/Campaigns/NewCampaignUX/steps/ConfigureProximityIQ';
import ChooseCampaignChannel from '../Client/Campaigns/NewCampaignUX/steps/ChooseCampaignChannel';
import SelectAudienceMethod from '../Client/Campaigns/NewCampaignUX/steps/SelectAudienceMethod';
import SelectTargetSegments from '../Client/Campaigns/NewCampaignUX/steps/SelectTargetSegments';
import BuildCustomAudience from '../Client/Campaigns/NewCampaignUX/steps/BuildCustomAudience';
import SMSTemplateDelivery from '../Client/Campaigns/NewCampaignUX/steps/SMSTemplateDelivery';
import EmailTemplateDelivery from '../Client/Campaigns/NewCampaignUX/steps/EmailTemplateDelivery';
import ReviewAndLaunch from '../Client/Campaigns/NewCampaignUX/steps/ReviewAndLaunch';
import ChooseAdPlatform from '../Client/Campaigns/NewCampaignUX/steps/ChooseAdPlatform';
import ConfigureMetaAds from '../Client/Campaigns/NewCampaignUX/steps/ConfigureMetaAds';
import ConfigureGoogleAds from '../Client/Campaigns/NewCampaignUX/steps/ConfigureGoogleAds';
import '../Client/Campaigns/NewCampaignUX/NewCampaignUX.css';
import './SuperAdminStyles.css';

const { Search } = Input;

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0 = Select Client/Agency, 1+ = Campaign wizard
  const [loading, setLoading] = useState(false);
  const [allEntities, setAllEntities] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Campaign data state (same as NewCampaignUX)
  const [campaignData, setCampaignData] = useState({
    selectedLocations: [],
    campaignType: null,
    selectedVehicles: [],
    dateRange: null,
    stopDuration: { min: 15, max: 180 },
    targetingRadius: 1,
    selectedStops: [],
    breadcrumbSpacing: 1,
    selectedRoutes: [],
    notificationRange: 1000,
    campaignChannel: null,
    audienceMethod: null,
    selectedSegments: [],
    customFilters: { service: [], geo: [] },
    campaignName: '',
    smsMessage: '',
    cooldownPeriod: '18months',
    quietHoursEnabled: true,
    quietHoursFrom: '9:00 PM',
    quietHoursTo: '8:00 AM',
    adPlatform: null,
    adminCreated: true,
    clientId: null
  });

  useEffect(() => {
    fetchClientsAndAgencies();
  }, []);


  const fetchClientsAndAgencies = async () => {
    setLoading(true);
    try {
      // Fetch clients
      const clientsResponse = await fetch('/api/clients', { headers: { ...authHeader() } });
      let clientsData = [];
      if (clientsResponse.ok) {
        const data = await clientsResponse.json();
        // API returns 'items' not 'clients'
        clientsData = (data.items || data.clients || []).map(client => ({
          ...client,
          type: 'client',
          displayName: client.name || client.user_full_name || client.user_email || client.email || 'Unnamed Client',
          email: client.user_email || client.email || '',
          status: client.status || (client.is_active ? 'active' : 'inactive')
        }));
      } else {
        console.error('Failed to fetch clients:', clientsResponse.status);
      }

      // Fetch enterprises
      const agenciesResponse = await fetch('/api/agencies', { headers: { ...authHeader() } });
      let agenciesData = [];
      if (agenciesResponse.ok) {
        const data = await agenciesResponse.json();
        // API returns 'items' not 'agencies'
        agenciesData = (data.items || data.agencies || []).map(agency => ({
          ...agency,
          type: 'enterprise',
          displayName: agency.name || agency.email || 'Unnamed Enterprise',
          email: agency.email || '',
          status: agency.status || 'active'
        }));
      } else {
        console.error('Failed to fetch enterprises:', agenciesResponse.status);
      }

      // Combine both
      setAllEntities([...clientsData, ...agenciesData]);
      
      if (clientsData.length === 0 && agenciesData.length === 0) {
        message.warning('No clients or enterprises found');
      } else {
        console.log(`Loaded ${clientsData.length} clients and ${agenciesData.length} enterprises`);
      }
    } catch (error) {
      console.error('Failed to fetch clients and enterprises:', error);
      message.error('Failed to fetch clients and enterprises');
    } finally {
      setLoading(false);
    }
  };

  const updateCampaignData = useCallback((updates) => {
    setCampaignData(prev => ({ ...prev, ...updates }));
  }, []);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    updateCampaignData({ 
      clientId: client.id,
      entityType: client.type,
      entityUserId: client.user_id || client.owner_user_id,
      entityName: client.displayName
    });
    setCurrentStep(1); // Move to location selection
  };

  const getTotalSteps = () => {
    if (campaignData.campaignType === 'stop') {
      if (campaignData.adPlatform === 'both') return 8;
      if (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'google') return 7;
      return 6;
    }
    if (campaignData.campaignType === 'route') {
      if (campaignData.adPlatform === 'both') return 8;
      if (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'google') return 7;
      return 6;
    }
    if (campaignData.campaignType === 'proximity') {
      return 9;
    }
    return 6;
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, getTotalSteps()));
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate('/dashboard/superadmin');
    } else if (currentStep === 1) {
      setCurrentStep(0);
      setSelectedClient(null);
      updateCampaignData({ selectedLocations: [], clientId: null });
    } else {
      setCurrentStep(prev => prev - 1);
    }
  };

  const filteredClients = allEntities.filter(entity => {
    const matchesSearch = searchText === '' || 
      (entity.displayName || entity.name || entity.email || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (entity.email || '').toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (entity.status || 'active').toLowerCase() === statusFilter.toLowerCase();
    
    const matchesType = typeFilter === 'all' || entity.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Render step based on currentStep
  const renderStep = () => {
    // Step 0: Select Client
    if (currentStep === 0) {
      return (
        <div className="new-campaign-ux">
          <div className="campaign-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined />
              <span>Back to Dashboard</span>
            </button>
            <div className="step-indicator">
              <span className="step-current">Step 1</span>
              <span className="step-total"> of {getTotalSteps()}</span>
            </div>
          </div>
          <div className="campaign-content">
            <div className="step-container">
              <div className="step-header">
                <div className="step-icon">
                  <TeamOutlined />
                </div>
                <h2 className="step-title">Select Client or Enterprise</h2>
                <p className="step-description">Choose a client or enterprise to create a campaign for</p>
              </div>

              <div className="step-body">
                <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <Search
                    placeholder="Search clients and enterprises..."
                    allowClear
                    style={{ flex: 1, minWidth: '250px' }}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Select
                    value={typeFilter}
                    onChange={setTypeFilter}
                    style={{ width: 150 }}
                  >
                    <Select.Option value="all">All Types</Select.Option>
                    <Select.Option value="client">Clients</Select.Option>
                    <Select.Option value="enterprise">Enterprises</Select.Option>
                  </Select>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 150 }}
                  >
                    <Select.Option value="all">All Status</Select.Option>
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="inactive">Inactive</Select.Option>
                  </Select>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: '60px' }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  <>
                    <Row gutter={[16, 16]}>
                      {filteredClients.map(entity => (
                        <Col xs={24} sm={12} lg={8} key={`${entity.type}-${entity.id}`}>
                          <Card
                            hoverable
                            onClick={() => handleClientSelect(entity)}
                            className="client-card"
                            style={{
                              border: '1px solid #f0f0f0',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '8px',
                                background: entity.type === 'enterprise' ? '#e6f7ff' : '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '20px',
                                color: entity.type === 'enterprise' ? '#1890ff' : '#595959'
                              }}>
                                {entity.type === 'enterprise' ? <UserOutlined /> : <TeamOutlined />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                                  {entity.displayName || entity.name || entity.email}
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '8px' }}>
                                  {entity.email}
                                </div>
                                <Space size={8}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    background: entity.type === 'enterprise' ? '#e6f7ff' : '#f0f5ff',
                                    color: entity.type === 'enterprise' ? '#1890ff' : '#597ef7',
                                    border: `1px solid ${entity.type === 'enterprise' ? '#91d5ff' : '#adc6ff'}`
                                  }}>
                                    {entity.type === 'enterprise' ? 'Enterprise' : 'Client'}
                                  </span>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    background: (entity.status || 'active') === 'active' ? '#f6ffed' : '#fff2e8',
                                    color: (entity.status || 'active') === 'active' ? '#52c41a' : '#fa8c16',
                                    border: `1px solid ${(entity.status || 'active') === 'active' ? '#b7eb8f' : '#ffd591'}`
                                  }}>
                                    {(entity.status || 'active')}
                                  </span>
                                </Space>
                                {entity.locations_count !== undefined && (
                                  <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                                    <EnvironmentOutlined /> {entity.locations_count} locations
                                  </div>
                                )}
                              </div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    <div style={{ marginTop: '24px', color: '#8c8c8c', fontSize: '14px' }}>
                      {filteredClients.length} {typeFilter === 'all' ? 'clients/enterprises' : typeFilter === 'client' ? 'clients' : 'enterprises'} available
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Step 1: Select Locations (using NewCampaignUX component)
    if (currentStep === 1) {
      return (
        <div className="new-campaign-ux">
          <div className="campaign-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined />
              <span>Back</span>
            </button>
            <div className="step-indicator">
              <span className="step-current">Step 2</span>
              <span className="step-total"> of {getTotalSteps()}</span>
            </div>
          </div>
          <div className="campaign-content">
            <SelectLocations
              selectedLocations={campaignData.selectedLocations}
              onUpdate={(locations) => updateCampaignData({ selectedLocations: locations })}
              onNext={handleNext}
              clientId={selectedClient?.id}
              entityType={selectedClient?.type}
              isAdminCreating={true}
            />
          </div>
        </div>
      );
    }

    // Step 2: Choose Campaign Type
    if (currentStep === 2) {
      return (
        <div className="new-campaign-ux">
          <div className="campaign-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined />
              <span>Back</span>
            </button>
            <div className="step-indicator">
              <span className="step-current">Step 3</span>
              <span className="step-total"> of {getTotalSteps()}</span>
            </div>
          </div>
          <div className="campaign-content">
            <ChooseCampaignType
              selectedType={campaignData.campaignType}
              onUpdate={(type) => updateCampaignData({ campaignType: type })}
              onNext={handleNext}
            />
          </div>
        </div>
      );
    }

    // Step 3: Configure based on campaign type
    if (currentStep === 3) {
      return (
        <div className="new-campaign-ux">
          <div className="campaign-header">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeftOutlined />
              <span>Back</span>
            </button>
            <div className="step-indicator">
              <span className="step-current">Step 4</span>
              <span className="step-total"> of {getTotalSteps()}</span>
            </div>
          </div>
          <div className="campaign-content">
            {campaignData.campaignType === 'stop' && (
              <ConfigureStopLogic
                campaignData={campaignData}
                onUpdate={updateCampaignData}
                onNext={handleNext}
                isAdminCreating={true}
                targetUserId={campaignData.entityUserId}
              />
            )}
            {campaignData.campaignType === 'route' && (
              <ConfigureRouteLogic
                campaignData={campaignData}
                onUpdate={updateCampaignData}
                onNext={handleNext}
                isAdminCreating={true}
                targetUserId={campaignData.entityUserId}
              />
            )}
            {campaignData.campaignType === 'proximity' && (
              <ConfigureProximityIQ
                campaignData={campaignData}
                onUpdate={updateCampaignData}
                onNext={handleNext}
                isAdminCreating={true}
                targetUserId={campaignData.entityUserId}
              />
            )}
          </div>
        </div>
      );
    }

    // Remaining steps follow the same pattern as NewCampaignUX
    // For brevity, I'll render the appropriate component based on step and campaign type
    return (
      <div className="new-campaign-ux">
        <div className="campaign-header">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeftOutlined />
            <span>Back</span>
          </button>
          <div className="step-indicator">
            <span className="step-current">Step {currentStep + 1}</span>
            <span className="step-total"> of {getTotalSteps()}</span>
          </div>
        </div>
        <div className="campaign-content">
          {/* Render remaining steps based on campaign type */}
          {currentStep === 4 && campaignData.campaignType === 'stop' && (
            <SelectStops 
              campaignData={campaignData} 
              onUpdate={updateCampaignData} 
              onNext={handleNext}
              isAdminCreating={true}
              targetUserId={campaignData.entityUserId}
            />
          )}
          {currentStep === 4 && campaignData.campaignType === 'route' && (
            <SelectRoutes 
              campaignData={campaignData} 
              onUpdate={updateCampaignData} 
              onNext={handleNext}
              isAdminCreating={true}
              targetUserId={campaignData.entityUserId}
            />
          )}
          {currentStep === 4 && campaignData.campaignType === 'proximity' && (
            <ChooseCampaignChannel selectedChannel={campaignData.campaignChannel} onUpdate={(channel) => updateCampaignData({ campaignChannel: channel })} onNext={handleNext} />
          )}
          {currentStep === 5 && (campaignData.campaignType === 'stop' || campaignData.campaignType === 'route') && (
            <ChooseAdPlatform selectedPlatform={campaignData.adPlatform} onUpdate={(platform) => updateCampaignData({ adPlatform: platform })} onNext={handleNext} campaignData={campaignData} />
          )}
          {currentStep === 5 && campaignData.campaignType === 'proximity' && (
            <SelectAudienceMethod selectedMethod={campaignData.audienceMethod} onUpdate={(method) => updateCampaignData({ audienceMethod: method })} onNext={handleNext} channel={campaignData.campaignChannel} />
          )}
          {currentStep === 6 && campaignData.campaignType === 'proximity' && campaignData.audienceMethod === 'quick' && (
            <SelectTargetSegments selectedSegments={campaignData.selectedSegments} onUpdate={updateCampaignData} onNext={handleNext} channel={campaignData.campaignChannel} />
          )}
          {currentStep === 6 && campaignData.campaignType === 'proximity' && campaignData.audienceMethod === 'custom' && (
            <BuildCustomAudience campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
          )}
          {currentStep === 6 && (campaignData.campaignType === 'route' || campaignData.campaignType === 'stop') && (campaignData.adPlatform === 'meta' || campaignData.adPlatform === 'both') && (
            <ConfigureMetaAds campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
          )}
          {currentStep === 6 && (campaignData.campaignType === 'route' || campaignData.campaignType === 'stop') && campaignData.adPlatform === 'google' && (
            <ConfigureGoogleAds campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
          )}
          {currentStep === 7 && campaignData.campaignType === 'proximity' && (
            campaignData.campaignChannel === 'email' ? (
              <EmailTemplateDelivery campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
            ) : (
              <SMSTemplateDelivery campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
            )
          )}
          {currentStep === 7 && (campaignData.campaignType === 'route' || campaignData.campaignType === 'stop') && campaignData.adPlatform === 'both' && (
            <ConfigureGoogleAds campaignData={campaignData} onUpdate={updateCampaignData} onNext={handleNext} />
          )}
          {currentStep === 8 && campaignData.campaignType === 'proximity' && (
            <ReviewAndLaunch campaignData={campaignData} />
          )}
        </div>
      </div>
    );
  };

  return renderStep();

};

export default CreateCampaign;
